import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Slide,
  FormGroup,
  FormControlLabel,
  Switch,
  Typography
} from '@mui/material'
import { H } from 'highlight.run'
import { ErrorBoundary } from '@highlight-run/react'

const CONSENT_KEY = 'micgro_consent_v1'

export default function CookieConsent({ onAcceptGA }) {
  const [open, setOpen] = useState(false)
  const [showManage, setShowManage] = useState(false)
  const [analyticsOk, setAnalyticsOk] = useState(true)
  const [replayOk, setReplayOk] = useState(true)

  // Load existing preferences on mount
  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY)
    if (stored) {
      const { analytics, replay } = JSON.parse(stored)
      setAnalyticsOk(analytics)
      setReplayOk(replay)
      setOpen(false)
      setShowManage(true)
      applyConsent(analytics, replay, false)
    } else {
      setOpen(true)
    }
  }, [])

  // Centralized consent handler
  const applyConsent = (analyticsChoice, replayChoice, persist = true) => {
    // 1) Update UI state
    setAnalyticsOk(analyticsChoice)
    setReplayOk(replayChoice)

    // 2) Persist & hide
    if (persist) {
      localStorage.setItem(
        CONSENT_KEY,
        JSON.stringify({ analytics: analyticsChoice, replay: replayChoice })
      )
      setOpen(false)
      setShowManage(true)
    }

    // 3) GA4
    if (analyticsChoice) {
      onAcceptGA()
    } else {
      window.gtag?.('consent', 'update', { analytics_storage: 'denied' })
      document.querySelector('script[src*="gtag"]')?.remove()
    }

    // 4) Highlight session replay

    if (replayChoice) {
      console.log('[CookieConsent] calling H.start()')
      H.start()
    } else {
      console.log('[CookieConsent] calling H.stop()')
      H.stop()
            if (persist) {
        setTimeout(() => window.location.reload(), 100)
      }
    }
  }

  const handleSave = () => applyConsent(analyticsOk, replayOk)
  const declineAll = () => applyConsent(false, false)

  return (
    <>
      {/* Sliding consent panel */}
      <Slide direction="up" in={open}>
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: 'background.alt',
            color: 'text.primary',
            boxShadow: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            borderTopLeftRadius: 1,
            borderTopRightRadius: 1,
            px: 2,
            py: 1,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            zIndex: theme => theme.zIndex.snackbar,
          }}
        >
          <FormGroup row sx={{ alignItems: 'center', gap: 2 }}>
            <Typography>We use cookies for:</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={analyticsOk}
                  onChange={e => setAnalyticsOk(e.target.checked)}
                />
              }
              label="Analytics (GA4)"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={replayOk}
                  onChange={e => setReplayOk(e.target.checked)}
                />
              }
              label="Session Replay"
            />
          </FormGroup>

          <Typography
            sx={{
              textAlign: 'center',
              color: 'warning.main',
              flexGrow: 1,
            }}
          >
            To participate in the Beta program, both options must be enabled.
            Disabling either will remove your eligibility for Beta access.
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={declineAll}>Decline</Button>
            <Button variant="contained" color="secondary" onClick={handleSave}>
              Save
            </Button>
          </Box>
        </Box>
      </Slide>

      {/* Manage panel toggle */}
      {showManage && !open && (
        <Box
          onClick={() => setOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 8,
            bgcolor: 'background.alt',
            color: 'text.primary',
            boxShadow: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            px: 1,
            py: 0.5,
            cursor: 'pointer',
            zIndex: theme => theme.zIndex.snackbar,
          }}
        >
          <Typography variant="caption">Manage Cookies</Typography>
        </Box>
      )}
    </>
  )
}
